import React, { useState, useEffect } from 'react';
import Button from '../../atoms/Button';
import Checkbox from '../../atoms/Checkbox';
import ModalConfirm from '../ModalConfirm';
import { useDispatch, useSelector } from 'react-redux';
import { getFilterList } from '../../../redux/operations/StoreFilterList';
import { storeFilterActions, modalConfirmActions } from '../../../redux/modules/Actions';
import { AppState } from '../../../redux/modules/store';
import { ModalConfirmState } from '../../../redux/reducers/ModalReducer';
import Icon from '@material-ui/core/Icon';
import StoreFilterManagement from '../../organisms/StoreFilterManagement';
import { FilterItem } from '../../../redux/reducers/StoreFilterReducer';
import { AgencyInfo } from '../../../redux/reducers/StoreSelectorReducer';
import { countActiveStores } from '../../../utils/StoreFilterList';
import { STORE_FILTER_SPOT_EVENT_MAPPING } from '../../../Common';

const FilterList: React.FC<{ isAgency?: boolean; className?: string }> = ({ isAgency = false, className = '' }) => {
  const dispatch = useDispatch();
  const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
  const filterStoreSetting = useSelector((state: AppState) => state.filterStore);
  const isOpen = useSelector((state: AppState) => state.storeSelector.isModalOpen);
  const { groupAgencies } = useSelector((state: AppState) => state.storeSelector);
  const { isLoading = false, data = [] } = filterStoreSetting;
  const toggleModal = () => {
    setIsOpenModal((prevState) => !prevState);
  };

  const onCloseModal = (childCallback?: () => void, handleCancelWithCheck?: boolean) => {
    const payload: ModalConfirmState = {
      isLoading: false,
      isOpen: true,
      isDanger: true,
      title: 'このページを離れますか？',
      type: 'left',
      msg: '保存していない内容は失われます。',
      primaryBtnText: '破棄して移動',
      callBack: () => {
        toggleModal();
        childCallback?.();
        dispatch(storeFilterActions.draftFilter.rollBack());
        dispatch(getFilterList(isAgency));
      },
    };
    if (handleCancelWithCheck) {
      dispatch(modalConfirmActions.trigglerModal(payload));
      dispatch(storeFilterActions.resetErr());
      return;
    }
    toggleModal();
    childCallback?.();
    dispatch(storeFilterActions.draftFilter.rollBack());
    dispatch(getFilterList(isAgency));
    dispatch(storeFilterActions.resetErr());
  };

  useEffect(() => {
    if (data.length) return;
    dispatch(getFilterList(isAgency));
  }, [isAgency]);

  useEffect(() => {
    dispatch(storeFilterActions.draftFilter.rollBack());
  }, [isOpen]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const mapping = isAgency ? STORE_FILTER_SPOT_EVENT_MAPPING['AGENCY'] : STORE_FILTER_SPOT_EVENT_MAPPING['EMPLOYEE'];
      const target = mapping['openEditModal'];
      if (target?.spotEvent && target?.className) {
        const elements = document.getElementsByClassName(target.className);
        Array.from(elements).forEach((el) => {
          el.setAttribute('onclick', target.spotEvent as string);
        });
      }
    }, 0);
    return () => clearTimeout(timer);
  }, [isAgency, data]);

  return (
    <div className='filter-modal__drilldown-item' style={{ padding: '0 10px', minWidth: 200, width: 200 }}>
      <Button
        className={className}
        width='full'
        label='店舗フィルタを編集'
        type='transparent'
        rightIcon='edit'
        onClick={toggleModal}
        data-testid-e2e='btn-open-modal-filter-store'
        data-testid='btnOpenModalFilterStore'
        style={{ display: 'flex', justifyContent: 'space-around' }}
      />
      <div className='filter-modal__scroll-area' style={{ overflowX: 'hidden' }}>
        <ul
          className='list filter-modal__scroll-area-store-filter-container'
          style={{ maxHeight: `calc(${isAgency ? '100vh' : '100svh'} - 240px` }}>
          <FilterListItems isLoading={isLoading} data={data} isAgency={isAgency} groupAgencies={groupAgencies} />
        </ul>
      </div>
      <StoreFilterManagement isAgency={isAgency} isOpenModal={isOpenModal} onCloseModal={onCloseModal} />
      <ModalConfirm />
    </div>
  );
};

type FilterListItemsProps = {
  isLoading: boolean;
  data: FilterItem[];
  isAgency: boolean;
  groupAgencies?: {
    [key: string]: AgencyInfo[];
  };
};

// 取得した店舗フィルタ一覧を画面へ反映する。
export const FilterListItems = (props: FilterListItemsProps): React.ReactElement => {
  const dispatch = useDispatch();
  const { isLoading, data, isAgency, groupAgencies } = props;
  if (isLoading) return <></>;
  if (!data.length)
    return (
      <li style={{ paddingLeft: 5 }} className='list__item-link list__item-link--has-right-icon list__item-link--has-left-icon'>
        フィルタがありません
      </li>
    );

  return (
    <>
      {data.map((item) => (
        <li
          key={item.filterID}
          data-testid={`filter-view-item`}
          style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingLeft: 5 }}>
          <Checkbox
            label={`${item.filterName}   (${countActiveStores(item.ordcstmCdList, isAgency, groupAgencies)})`}
            name={item.filterName}
            disabled={!item.isExist}
            data-testid={`${item.filterID}-${item.filterName}`}
            checked={item.checked}
            onChange={() => {
              dispatch(storeFilterActions.applyCheckboxFilter(item));
            }}
          />
          {!item.isExist && (
            <Icon color='error' style={{ marginRight: 5 }}>
              error
            </Icon>
          )}
        </li>
      ))}
    </>
  );
};

export default FilterList;
